import React from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, TrendingDown, DollarSign, FileWarning, Lightbulb } from 'lucide-react';

const ReportBasic = ({ report }) => {
  // Determina colore e icona del verdetto
  const getVerdettoStyle = (giudizio) => {
    if (!giudizio) return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
    
    if (giudizio.includes('Va bene')) {
      return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', icon: CheckCircle };
    } else if (giudizio.includes('Migliorabile')) {
      return { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300', icon: AlertTriangle };
    } else if (giudizio.includes('ATTENZIONE')) {
      return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', icon: XCircle };
    }
    return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
  };

  const verdetto = report.verdetto_immediato || {};
  const verdettoStyle = getVerdettoStyle(verdetto.giudizio);
  const VerdettoIcon = verdettoStyle.icon || Shield;

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none">
      {/* Header con Logo */}
      <div className="bg-gradient-to-r from-blue-600 to-green-500 p-8 text-white">
        <div className="flex items-start justify-between mb-6">
          <div className="bg-white p-2 rounded-lg">
            <img 
              src="https://customer-assets.emergentagent.com/job_clauscheck/artifacts/llmpg64m_ChatGPT%20Image%208%20mar%202026%2C%2018_40_06.png" 
              alt="PRIMA LEGGO" 
              className="h-16 w-auto"
            />
          </div>
          <div className="text-right">
            <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full mb-2">
              <span className="font-bold text-lg">REPORT BASIC</span>
            </div>
            <p className="text-sm opacity-90">{report.categoria || 'Contratto'}</p>
          </div>
        </div>

        <div className="text-sm">
          <p className="opacity-75 mb-1">Contratto analizzato</p>
          <p className="font-bold text-xl">{report.file_name || report.contract_name || 'N/A'}</p>
        </div>
      </div>

      {/* VERDETTO IMMEDIATO */}
      <div className={`p-8 border-b-4 ${verdettoStyle.border} ${verdettoStyle.bg}`}>
        <div className="flex items-start space-x-4">
          <div className={`${verdettoStyle.text} mt-1`}>
            <VerdettoIcon className="w-12 h-12" />
          </div>
          <div className="flex-1">
            <h2 className={`text-3xl font-black mb-3 ${verdettoStyle.text}`}>
              {verdetto.giudizio || 'Analisi completata'}
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              {verdetto.spiegazione || 'Contratto analizzato con successo.'}
            </p>
          </div>
        </div>
      </div>

      {/* RISCHI PRINCIPALI */}
      <div className="p-8 bg-white border-b">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <AlertTriangle className="w-6 h-6 mr-2 text-red-600" />
          Rischi Principali
        </h2>
        <div className="space-y-3">
          {report.rischi_principali && report.rischi_principali.length > 0 ? (
            report.rischi_principali.map((rischio, idx) => (
              <div key={idx} className="flex items-start space-x-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                <span className="text-red-600 font-bold text-lg">{idx + 1}.</span>
                <p className="text-gray-800 flex-1">{rischio}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">Nessun rischio critico identificato.</p>
          )}
        </div>
      </div>

      {/* CLAUSOLE PERICOLOSE */}
      <div className="p-8 bg-gray-50 border-b">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <FileWarning className="w-6 h-6 mr-2 text-orange-600" />
          Clausole da Tenere d'Occhio
        </h2>
        <div className="space-y-4">
          {report.clausole_pericolose && report.clausole_pericolose.length > 0 ? (
            report.clausole_pericolose.map((clausola, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-gray-900">{clausola.titolo}</h3>
                  <span className="text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-semibold">
                    {clausola.dove || 'Ubicazione non specificata'}
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-gray-700">
                    <span className="font-semibold text-orange-600">Cosa fa:</span> {clausola.cosa_fa}
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold text-red-600">Impatto:</span> {clausola.impatto}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">Nessuna clausola particolarmente problematica identificata.</p>
          )}
        </div>
      </div>

      {/* COSTI NASCOSTI */}
      <div className="p-8 bg-white border-b">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <DollarSign className="w-6 h-6 mr-2 text-green-600" />
          Costi Nascosti o Sospetti
        </h2>
        <div className="space-y-2">
          {report.costi_nascosti && report.costi_nascosti.length > 0 ? (
            report.costi_nascosti.map((costo, idx) => (
              <div key={idx} className="flex items-start space-x-3 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                <span className="text-yellow-600 font-bold">💰</span>
                <p className="text-gray-800 flex-1">{costo}</p>
              </div>
            ))
          ) : (
            <p className="text-green-600 font-semibold">✅ Nessun costo nascosto identificato</p>
          )}
        </div>
      </div>

      {/* COSA FARE */}
      <div className="p-8 bg-gradient-to-br from-blue-50 to-green-50 border-b-4 border-blue-400">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
          <Lightbulb className="w-6 h-6 mr-2 text-blue-600" />
          Cosa Fare Adesso
        </h2>
        {report.cosa_fare ? (
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="mb-4">
              <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-lg">
                {report.cosa_fare.azione}
              </span>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              {report.cosa_fare.perche}
            </p>
          </div>
        ) : (
          <p className="text-gray-500">Nessuna azione specifica consigliata.</p>
        )}
      </div>

      {/* SPIEGAZIONE SEMPLICE */}
      <div className="p-8 bg-white">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          📝 In Parole Semplici
        </h2>
        <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-blue-500">
          <p className="text-gray-800 text-lg leading-relaxed">
            {report.spiegazione_semplice || 'Contratto analizzato. Leggi attentamente le sezioni sopra.'}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-100 p-6 text-center text-sm text-gray-600">
        <p className="font-semibold">⚠️ Questo report NON sostituisce la consulenza legale professionale</p>
        <p className="mt-2">Per questioni legali complesse, consulta sempre un avvocato qualificato.</p>
      </div>
    </div>
  );
};

export default ReportBasic;
